"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import { buttonVariants } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";
import { Info } from "@/components/icons";
import { trackEvent } from "@/lib/posthog-client";
import { COMPARISON_ROWS, PRICING_ROW, USE_CASE_ROW } from "./data";
import type { ComparisonVariant, UseCaseItem } from "./types";

interface Props {
  variant: ComparisonVariant;
}

export default function ProxyComparisonSection({ variant }: Props) {
  const prefersReducedMotion = useReducedMotion();

  function handlePricingClick(target: ComparisonVariant) {
    trackEvent("proxy_comparison_pricing_clicked", { from: variant, to: target });
  }

  function handleUseCaseClick(useCase: string) {
    trackEvent("proxy_comparison_use_case_clicked", { from: variant, useCase });
  }

  function handleContactSupportClick() {
    trackEvent("proxy_comparison_contact_support_clicked", { from: variant });
  }

  function renderUseCaseItems(items: UseCaseItem[]) {
    return items.map((item, i) => (
      <span key={item.text}>
        {item.href ? (
          <Link
            href={item.href}
            onClick={() => handleUseCaseClick(item.text)}
            className="underline-offset-4 hover:underline"
          >
            {item.text}
          </Link>
        ) : (
          item.text
        )}
        {i < items.length - 1 && ", "}
      </span>
    ));
  }

  return (
    <section
      id="compare-proxies"
      className="py-section border-border border-b"
      aria-labelledby="compare-proxies-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5 }}
        >
          <SectionHeader
            eyebrow="Comparing Proxy Types?"
            id="compare-proxies-heading"
            heading={
              variant === "residential"
                ? "Residential Proxies vs. Mobile Proxies"
                : "Mobile vs. Residential Proxies"
            }
            body="Both proxy types can handle many of the same tasks. Mobile proxies are best when you specifically need a mobile network identity, while residential proxies are usually more cost-effective for scalable web scraping and data collection."
          />

          <div className="border-border mt-10 overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-border bg-foreground/[0.03] border-b">
                  <th scope="col" className="text-muted-foreground px-5 py-3.5 font-medium">
                    Consideration
                  </th>
                  <th scope="col" className="text-foreground px-5 py-3.5 font-semibold">
                    Mobile Proxies
                  </th>
                  <th scope="col" className="text-foreground px-5 py-3.5 font-semibold">
                    Residential Proxies
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-border bg-foreground/[0.03] border-b">
                  <th
                    scope="row"
                    className="text-muted-foreground px-5 py-3.5 font-medium whitespace-nowrap"
                  >
                    {USE_CASE_ROW.label}
                  </th>
                  <td className="text-foreground/90 px-5 py-3.5">
                    {renderUseCaseItems(USE_CASE_ROW.mobile)}
                  </td>
                  <td className="text-foreground/90 px-5 py-3.5">
                    {renderUseCaseItems(USE_CASE_ROW.residential)}
                  </td>
                </tr>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label} className="border-border border-b last:border-b-0">
                    <th
                      scope="row"
                      className="text-muted-foreground px-5 py-3.5 font-medium whitespace-nowrap"
                    >
                      {row.label}
                    </th>
                    <td className="text-foreground/90 px-5 py-3.5">{row.mobile}</td>
                    <td className="text-foreground/90 px-5 py-3.5">{row.residential}</td>
                  </tr>
                ))}
                <tr className="bg-foreground/[0.03]">
                  <th
                    scope="row"
                    className="text-muted-foreground px-5 py-4 font-medium whitespace-nowrap"
                  >
                    {PRICING_ROW.label}
                  </th>
                  <td className="px-5 py-4">
                    <Link
                      href={PRICING_ROW.mobileHref}
                      onClick={() => handlePricingClick("mobile")}
                      className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                    >
                      See Mobile Pricing
                    </Link>
                  </td>
                  <td className="px-5 py-4">
                    <Link
                      href={PRICING_ROW.residentialHref}
                      onClick={() => handlePricingClick("residential")}
                      className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                    >
                      See Residential Pricing
                    </Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="border-border bg-card mt-6 flex items-start gap-3 rounded-xl border p-5">
            <Info size={18} className="text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <h3 className="text-foreground text-sm font-semibold">
                Planning to scrape websites?
              </h3>
              <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                Mobile proxies can be used for web scraping, but residential proxies are generally
                the better-value option for high-volume scraping, price monitoring, SEO monitoring,
                and AI data collection. Choose mobile proxies when the target specifically requires
                traffic from a mobile network.
              </p>
            </div>
          </div>

          <p className="text-muted-foreground mt-6 text-center text-sm">
            Still not sure which one fits?{" "}
            <Link
              href={ROUTES.contactSupport}
              onClick={handleContactSupportClick}
              className="text-foreground font-medium underline underline-offset-4 transition-opacity hover:opacity-80"
            >
              Contact support
            </Link>{" "}
            or start a live chat, and we&apos;ll help you decide.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
